/**
 * COLLECTION ADD MODALS
 *
 * Renders the appropriate add item modal based on the current filter or selected category.
 * Extracted from Collection.tsx to reduce JSX tree complexity.
 */

import type { CollectionItem } from '../../types';
import type { BrandCategory } from '../../types/brandCategories';
import AddPolishModal from '../polish/AddPolishModal';
import AddPlateModal from '../plates/AddPlateModal';
import AddDecalModal from '../decals/AddDecalModal';
import AddEmbellishmentModal from '../embellishments/AddEmbellishmentModal';
import AddToolModal from '../tools/AddToolModal';
import AddBuffingPowderModal from '../powders/AddBuffingPowderModal';
import AddAcrylicModal from '../acrylics/AddAcrylicModal';
import AddCuticleCareModal from '../cuticleCare/AddCuticleCareModal';
import AddItemPickerModal from '../AddItemPickerModal';

interface CollectionAddModalsProps {
  showAddModal: boolean;
  mainFilter: string;
  addItemCategory: string | null;
  showItemPicker: boolean;
  onCloseAddModal: () => void;
  onCloseWithCategory: () => void;
  onItemAdded: (item: CollectionItem) => void;
  onCloseItemPicker: () => void;
  onSelectCategory: (category: string) => void;
  // Brand autocomplete props
  existingBrands?: string[];
  userBrandCategories?: Record<string, BrandCategory>;
}

export default function CollectionAddModals({
  showAddModal,
  mainFilter,
  addItemCategory,
  showItemPicker,
  onCloseAddModal,
  onCloseWithCategory,
  onItemAdded,
  onCloseItemPicker,
  onSelectCategory,
  existingBrands = [],
  userBrandCategories = {},
}: CollectionAddModalsProps) {
  // Render modal based on mainFilter (when on a specific category page)
  const renderFilterBasedModal = () => {
    if (!showAddModal || mainFilter === 'all') return null;

    switch (mainFilter) {
      case 'polish':
        return (
          <AddPolishModal
            onClose={onCloseAddModal}
            onSuccess={onItemAdded}
            existingBrands={existingBrands}
            userBrandCategories={userBrandCategories}
          />
        );
      case 'plate':
        return <AddPlateModal onClose={onCloseAddModal} onSuccess={onItemAdded} />;
      case 'transfer':
        return <AddDecalModal onClose={onCloseAddModal} onSuccess={onItemAdded} />;
      case 'embellishment':
        return <AddEmbellishmentModal onClose={onCloseAddModal} onSuccess={onItemAdded} />;
      case 'tool':
        return <AddToolModal onClose={onCloseAddModal} onSuccess={onItemAdded} existingBrands={existingBrands} userBrandCategories={userBrandCategories} />;
      case 'powder':
        return <AddBuffingPowderModal onClose={onCloseAddModal} onSuccess={onItemAdded} />;
      case 'acrylic':
        return <AddAcrylicModal onClose={onCloseAddModal} onSuccess={onItemAdded} />;
      case 'cuticle_care':
        return <AddCuticleCareModal onClose={onCloseAddModal} onSuccess={onItemAdded} />;
      default:
        return null;
    }
  };

  // Render modal based on addItemCategory (when on "All Items" page)
  const renderCategoryBasedModal = () => {
    if (!showAddModal || mainFilter !== 'all' || !addItemCategory) return null;

    switch (addItemCategory) {
      case 'polish':
        return (
          <AddPolishModal
            onClose={onCloseWithCategory}
            onSuccess={onItemAdded}
            existingBrands={existingBrands}
            userBrandCategories={userBrandCategories}
          />
        );
      case 'plate':
        return <AddPlateModal onClose={onCloseWithCategory} onSuccess={onItemAdded} />;
      case 'transfer':
        return <AddDecalModal onClose={onCloseWithCategory} onSuccess={onItemAdded} />;
      case 'embellishment':
        return <AddEmbellishmentModal onClose={onCloseWithCategory} onSuccess={onItemAdded} />;
      case 'tool':
        return <AddToolModal onClose={onCloseWithCategory} onSuccess={onItemAdded} existingBrands={existingBrands} userBrandCategories={userBrandCategories} />;
      case 'powder':
        return <AddBuffingPowderModal onClose={onCloseWithCategory} onSuccess={onItemAdded} />;
      case 'acrylic':
        return <AddAcrylicModal onClose={onCloseWithCategory} onSuccess={onItemAdded} />;
      case 'cuticle_care':
        return <AddCuticleCareModal onClose={onCloseWithCategory} onSuccess={onItemAdded} />;
      default:
        return null;
    }
  };

  return (
    <>
      {renderFilterBasedModal()}
      {renderCategoryBasedModal()}
      {showItemPicker && (
        <AddItemPickerModal
          onClose={onCloseItemPicker}
          onSelectCategory={onSelectCategory}
        />
      )}
    </>
  );
}
